import React, { useState } from "react"
import { Card, Container, Divider, Form, Menu, Message, Segment } from "semantic-ui-react"
import parkLogoPath from "../../assets/images/park_logo__green.svg"
import { PasswordInput } from "./ui/Admin/Form/PasswordInput"
import { z } from "zod"
import useAppForm from "~/hooks/useAppForm"
import { usePasswordRules, passwordSchema } from "~/hooks/usePasswordRules"
import { Controller } from "react-hook-form"
import Auth from "~/api2/auth"

interface componentProps {
  authenticity_token: string
  email: string
  token: string
}

export const passwordWithConfirmationSchema = z
  .object({
    password: passwordSchema,
    passwordConfirmation: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: "Passwords do not match",
    path: ["passwordConfirmation"],
  })

type UpdatePasswordFormType = z.infer<typeof passwordWithConfirmationSchema>

const UpdatePassword = (props: componentProps) => {
  const [serverError, setServerError] = useState<string | null>(null)
  const [serverSuccess, setServerSuccess] = useState<string | null>(null)
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid, isSubmitting, touchedFields },
    reset,
  } = useAppForm<UpdatePasswordFormType>({
    defaultValues: {
      password: "",
      passwordConfirmation: "",
    },
    schema: passwordWithConfirmationSchema,
    mode: "onChange",
  })

  const onSubmit = async (data: UpdatePasswordFormType) => {
    setServerError(null)
    setServerSuccess(null)
    try {
      const result = await Auth.updateAdminPassword(
        props.authenticity_token,
        data.password,
        data.passwordConfirmation,
        props.token,
      )
      if (result.success) {
        setServerSuccess(result.message)
        reset()
        setTimeout(() => {
          window.location.href = result.redirect_url
        }, 2000)
      }
    } catch (err: any) {
      setServerError("Failed to update password. " + (err?.message || "Unknown error"))
    }
  }

  return (
    <>
      <Segment>
        <Container>
          <Menu secondary>
            <span className="flex-inline align-middle mr-2">
              <img src={parkLogoPath} className="inline z-10 h-10" />
            </span>
          </Menu>
        </Container>
      </Segment>
      <Container>
        <Segment basic>
          <Card.Group centered>
            <div className="max-w-lg">
              <Card fluid>
                <Card.Content>
                  <Card.Header>Change your password</Card.Header>
                  <p>Create a new password for your account, {props.email}, and confirm it below.</p>
                  <Divider />
                  {serverError && <Message negative content={serverError} />}
                  {serverSuccess && <Message positive content={serverSuccess} />}
                  <Form onSubmit={handleSubmit(onSubmit)} loading={isSubmitting}>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => {
                        const value = field.value || ""
                        const showRules = touchedFields.password && isDirty && value.length > 0
                        const [failedRules, rules] = usePasswordRules(value)
                        return (
                          <div>
                            <PasswordInput
                              {...field}
                              label="Password"
                              name="admin_user[password]"
                              required
                              icon={
                                value && failedRules.length === 0
                                  ? { name: "check circle", color: "green" }
                                  : { name: "key" }
                              }
                              placeholder={"Password"}
                              error={false}
                            />
                            {showRules && (
                              <div style={{ marginTop: 4 }}>
                                <ul style={{ paddingLeft: 18, margin: 0 }}>
                                  {rules.map((rule) => (
                                    <li
                                      key={rule.message}
                                      style={{
                                        color: rule.test(value) ? "green" : "#b4710a",
                                        fontSize: 13,
                                        listStyleType: "disc",
                                      }}
                                    >
                                      {rule.message}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )
                      }}
                    />

                    <Controller
                      name="passwordConfirmation"
                      control={control}
                      render={({ field }) => {
                        const value = field.value || ""
                        const showError =
                          touchedFields.passwordConfirmation &&
                          isDirty &&
                          value.length > 0 &&
                          errors.passwordConfirmation
                        return (
                          <div>
                            <PasswordInput
                              {...field}
                              label="Password Confirmation"
                              name="admin_user[password_confirmation]"
                              required
                              icon={
                                value && !errors.passwordConfirmation
                                  ? { name: "check circle", color: "green" }
                                  : { name: "key" }
                              }
                              placeholder={"Retype password"}
                              error={false}
                            />
                            {showError && (
                              <Message negative size="tiny" content={errors.passwordConfirmation?.message as string} />
                            )}
                          </div>
                        )
                      }}
                    />

                    <Form.Button fluid primary disabled={!isDirty || !isValid || isSubmitting} className="pt-2">
                      Update Password
                    </Form.Button>
                  </Form>
                </Card.Content>
              </Card>
            </div>
          </Card.Group>
        </Segment>
      </Container>
    </>
  )
}

export default UpdatePassword
